/**

 * API Service for Thaklis Integration

 * Handles all HTTP requests to the Thaklis backend

 */



import axios from 'axios';
import * as Sentry from '@sentry/react';

import apiConfig from '../config/api.config';
import { reportServerReachable } from './connection-status';

const JWT_TOKEN_KEY = 'jwt_token';

// On an auth failure (expired/invalid session), clear the current section's tokens and send the user
// to the matching login page. The section is derived from the current path so portal/group users go
// to their own login, not the TC login. Called from the 401 response interceptor.
const clearSessionAndRedirect = () => {
  const path = window.location.pathname || '';
  if (path.startsWith('/portal')) {
    localStorage.removeItem('portalAuthenticated');
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalOrganization');
    if (!path.startsWith('/portal/login') && !path.startsWith('/portal/auto-login')) {
      window.location.href = '/portal/login';
    }
  } else if (path.startsWith('/group')) {
    localStorage.removeItem('groupAuthenticated');
    localStorage.removeItem('groupToken');
    localStorage.removeItem('groupUser');
    localStorage.removeItem('groupData');
    if (!path.startsWith('/group/login')) {
      window.location.href = '/group/login';
    }
  } else {
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    if (!path.startsWith('/login')) {
      window.location.href = '/login';
    }
  }
};



// Create axios instance with base configuration

const apiClient = axios.create({

  baseURL: apiConfig.baseURL,

  timeout: apiConfig.timeout,

  headers: apiConfig.headers

});



// Request interceptor to add authentication

apiClient.interceptors.request.use(

  async (config) => {

    // When calling the Node.js backend, authentication is handled by the backend

    // No need for frontend to authenticate with Thaklis directly

    console.log('📡 [API] Request to:', config.url);

    // Don't overwrite if Authorization header is already explicitly set (e.g., portal APIs)
    const hasExistingAuth = config.headers && config.headers.Authorization;
    const path = window.location.pathname || '';
    // Portal and group pages must send their own token; never fall back to the staff token.
    const isStaffSection = !path.startsWith('/portal') && !path.startsWith('/group');
    if (!hasExistingAuth && isStaffSection) {
      const token = localStorage.getItem(JWT_TOKEN_KEY);
      const isLoginRequest = config?.url?.includes(apiConfig.endpoints.login);
      if (token && !isLoginRequest) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Tenant context — backend derives its base host from
    // app.managers.frontend.url and decides platform vs tenant from this value.
    // Never overwrite if already explicitly set by a specific call site.
    config.headers = config.headers || {};
    if (!config.headers['X-Tenant-Domain']) {
      config.headers['X-Tenant-Domain'] = window.location.hostname;
    }

    return config;

  },

  (error) => {

    return Promise.reject(error);

  }

);



// Response interceptor to handle errors

apiClient.interceptors.response.use(

  (response) => {

    // A real response means the server is reachable — drives the header status indicator.
    reportServerReachable(true);

    return response;

  },

  (error) => {

    // A response (even an error status) means reachable; no response = offline.
    reportServerReachable(!!error.response);

    if (error.response) {

      // Server responded with error status

      switch (error.response.status) {

        case 401:

          // Unauthorized - token expired or invalid

          // Don't redirect if this is a login endpoint itself failing (bad credentials)

          if (error.config?.url?.includes('/auth/login')) {

            console.error('🚫 [API] Login failed - invalid credentials');

            break;

          }



          // Session expired — clear this section's tokens and go to the matching login page
          // (TC, portal, or group), so portal/group users aren't sent to the TC login.

          console.error('🚫 [API] 401 Unauthorized - clearing session and redirecting to login');

          clearSessionAndRedirect();

          break;

        case 403:

          // Forbidden

          console.error('🚫 [API] 403 Forbidden:', error.response.data);

          break;

        case 404:

          // Not found

          console.error('🚫 [API] 404 Not found:', error.response.data);

          break;

        case 500:

          // Server error

          console.error('🚫 [API] 500 Server error:', error.response.data);
          Sentry.captureException(error, {
            tags: { api_status: 500, api_url: error.config?.url },
            extra: { response_data: error.response.data, method: error.config?.method },
          });

          break;

        default:

          console.error('🚫 [API] Error:', error.response.status, error.response.data);
          if (error.response.status >= 500) {
            Sentry.captureException(error, {
              tags: { api_status: error.response.status, api_url: error.config?.url },
            });
          }

      }

    } else if (error.request) {

      // Request made but no response received

      console.error('🚫 [API] No response from server:', error.request);
      Sentry.captureException(error, {
        tags: { error_type: 'network_error' },
        extra: { url: error.config?.url },
      });

    } else {

      // Error in request setup

      console.error('🚫 [API] Request setup error:', error.message);

    }

    return Promise.reject(error);

  }

);



// API Service methods

// Server caps page size at 200; pickers read a single page of that size.
const ORG_PICKER_PAGE_SIZE = 200;

const ApiService = {

  // Generic methods

  get: (url, config = {}) => apiClient.get(url, config),

  post: (url, data, config = {}) => apiClient.post(url, data, config),

  put: (url, data, config = {}) => apiClient.put(url, data, config),

  delete: (url, config = {}) => apiClient.delete(url, config),

  patch: (url, data, config = {}) => apiClient.patch(url, data, config),



  // Authentication

  login: async (username, password) => {

    console.log('🔐 [API] Login attempt for user:', username);

    try {

      const response = await apiClient.post(apiConfig.endpoints.login, { username, password });
      const token = response?.data?.data?.token || response?.data?.token;
      if (token) {
        localStorage.setItem(JWT_TOKEN_KEY, token);
      }



      return response;

    } catch (error) {

      console.error('❌ [API] Login failed:', error);

      throw error;

    }

  },



  logout: () => {

    console.log('🚪 [API] Logout - clearing tokens');

    localStorage.removeItem(JWT_TOKEN_KEY);

    localStorage.removeItem('isAuthenticated');

    localStorage.removeItem('currentUser');

    return Promise.resolve();

  },



  // Typing Centers

  getTypingCenters: () => {

    return apiClient.get(apiConfig.endpoints.typingCenters);

  },



  getTypingCenterById: (id) => {

    return apiClient.get(apiConfig.endpoints.typingCenterById(id));

  },



  createTypingCenter: (data) => {

    return apiClient.post(apiConfig.endpoints.typingCenters, data);

  },



  updateTypingCenter: (id, data) => {

    return apiClient.put(apiConfig.endpoints.typingCenterById(id), data);

  },



  deleteTypingCenter: (id) => {

    return apiClient.delete(apiConfig.endpoints.typingCenterById(id));

  },



  getTypingCenterUsers: (id) => {

    return apiClient.get(apiConfig.endpoints.typingCenterUsers(id));

  },



  // Organizations

  getOrganizations: (type) => {

    // If type is provided, fetch by type, otherwise fetch all

    const endpoint = type

      ? apiConfig.endpoints.organizationsByType(type)

      : apiConfig.endpoints.organizations;

    return apiClient.get(endpoint);

  },



  getOrganizationCustomers: () => {

    return apiClient.get(apiConfig.endpoints.organizationCustomers);

  },



  getOrganizationDetails: (organizationId) => {

    return apiClient.get(apiConfig.endpoints.organizationDetails(organizationId));

  },



  getOrganizationKeyValues: (type) => {

    return apiClient.get(apiConfig.endpoints.organizationKeyValues(type));

  },



  getOrganizationLogo: (orgId) => {

    return apiClient.get(apiConfig.endpoints.organizationLogo(orgId), {

      responseType: 'blob'

    });

  },

  downloadFile: (path) => {
    return apiClient.get('/api/files/download', {
      params: { path },
      responseType: 'blob'
    });
  },



  // Typing Center Mappings

  // Organization Configuration (per typing center mapping)

  getOrganizationConfig: (mappingId) => {

    return apiClient.get(apiConfig.endpoints.organizationConfig(mappingId));

  },



  getOrganizationConfigByTcAndOrg: (typingCenterId, organizationId) => {

    return apiClient.get(apiConfig.endpoints.organizationConfigByTcAndOrg(typingCenterId, organizationId));

  },



  updateOrganizationConfig: (mappingId, configData) => {

    return apiClient.put(apiConfig.endpoints.organizationConfig(mappingId), configData);

  },

  // Organization Credentials (Thaklis auth per mapping)

  setOrganizationCredentials: (mappingId, credentials) => {
    return apiClient.put(apiConfig.endpoints.organizationCredentials(mappingId), credentials);
  },

  deleteOrganizationCredentials: (mappingId) => {
    return apiClient.delete(apiConfig.endpoints.organizationCredentials(mappingId));
  },

  getOrganizationCredentialStatus: (mappingId) => {
    return apiClient.get(apiConfig.endpoints.organizationCredentialStatus(mappingId));
  },

  testOrganizationCredentials: (mappingId) => {
    return apiClient.post(apiConfig.endpoints.organizationCredentialTest(mappingId));
  },

  getOrganizationToken: (mappingId) => {
    return apiClient.get(apiConfig.endpoints.organizationCredentialToken(mappingId));
  },
  // Internal Organizations (Customer Organizations)
  /** Customer listing. Params: typingCenterId, type (CLIENT|TYPING_CENTER),
   *  search (name or TRN), page, size. Response data is
   *  { content, page, size, total_elements, total_pages, counts: { clients, typing_centers } }. */
  getInternalOrganizationsPage: (params) => {
    return apiClient.get('/api/internal-organizations', { params });
  },

  /** Wallet balances for just the customer orgs on the current page. Max 200 ids. */
  getCustomerWalletBalances: (internalOrganizationIds, typingCenterId) => {
    const params = {};
    if (typingCenterId) params.typingCenterId = typingCenterId;
    return apiClient.post('/api/customer-wallets/balances', internalOrganizationIds, { params });
  },

  /** Every active customer of a typing center, for pickers and filter dropdowns.
   *  Reads one page at the server's maximum and returns the rows, so callers keep
   *  receiving a plain array at `response.data.data`. */
  getInternalOrganizations: async (typingCenterId) => {
    const response = await apiClient.get('/api/internal-organizations', {
      params: { typingCenterId, page: 0, size: ORG_PICKER_PAGE_SIZE }
    });
    const pageData = response?.data?.data || {};
    const content = Array.isArray(pageData.content) ? pageData.content : [];
    return { ...response, data: { ...response.data, data: content, count: content.length } };
  },

  // Tickets

  getTickets: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.tickets, { params });

  },



  getTicketById: (id) => {

    return apiClient.get(apiConfig.endpoints.ticketById(id));

  },



  // Billing

  getWallets: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.wallets, { params });

  },



  getPayments: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.payments, { params });

  },



  getWalletBalance: (organizationId) => {

    return apiClient.get(`/api/organizations/${organizationId}/wallet`);

  },



  getCustomerPayments: (customerId) => {

    return apiClient.get(apiConfig.endpoints.customerPayments(customerId));

  },



  // Dashboard

  getDashboardStats: () => {

    return apiClient.get(apiConfig.endpoints.dashboardStats);

  },



  getFinancialSummary: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.dashboardFinancialSummary, { params });

  },



  getTopServices: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.dashboardTopServices, { params });

  },



  getInvoiceAging: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.invoiceAging, { params });

  },



  getTodaySummary: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.dashboardTodaySummary, { params });

  },



  getOperationsSummary: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.dashboardOperationsSummary, { params });

  },



  getRecentActivity: () => {

    return apiClient.get(apiConfig.endpoints.recentActivity);

  },



  // Organization Payments (Pro Portal)

  getOrganizationPayments: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.organizationPayments, { params });

  },



  getPaymentsViaProSummary: (typingCenterId) => {

    return apiClient.get(apiConfig.endpoints.paymentsViaProSummary, { params: { typingCenterId } });

  },



  getOrganizationPaymentById: (id) => {

    return apiClient.get(apiConfig.endpoints.organizationPaymentById(id));

  },



  createOrganizationPayment: (paymentData) => {

    return apiClient.post(apiConfig.endpoints.createOrganizationPayment, paymentData);

  },

  // Available-to-debit snapshot for a TC source bank account (offline auto-debit).
  getSourceBankAvailable: (typingCenterId, bankAccountId) => {

    return apiClient.get('/api/payments/source-bank-available', { params: { typingCenterId, bankAccountId } });

  },



  updateOrganizationPayment: (id, paymentData) => {

    return apiClient.put(apiConfig.endpoints.updateOrganizationPayment(id), paymentData);

  },



  topupWalletFromPayment: (id, topupData) => {

    return apiClient.post(apiConfig.endpoints.topupWalletFromPayment(id), topupData);

  },



  getTypingCenterPaymentSummary: (typingCenterId, params = {}) => {

    return apiClient.get(apiConfig.endpoints.typingCenterPaymentSummary(typingCenterId), { params });

  },



  getTypingCenterPaymentsByOrganization: (typingCenterId, params = {}) => {

    return apiClient.get(apiConfig.endpoints.typingCenterPaymentsByOrganization(typingCenterId), { params });

  },





  // Thaklis bank accounts (proxy to Thaklis Main)

  getAvailableBankAccounts: () => {

    return apiClient.get(apiConfig.endpoints.availableBankAccounts);

  },



  // Typing Center Bank Accounts (Managers-owned)

  getTcBankAccounts: (typingCenterId) => {

    const params = typingCenterId ? { typingCenterId } : {};

    return apiClient.get(apiConfig.endpoints.tcBankAccounts, { params });

  },



  getTcBankAccount: (id) => {

    return apiClient.get(apiConfig.endpoints.tcBankAccountById(id));

  },



  createTcBankAccount: (data) => {

    return apiClient.post(apiConfig.endpoints.tcBankAccounts, data);

  },



  updateTcBankAccount: (id, data) => {

    return apiClient.put(apiConfig.endpoints.tcBankAccountById(id), data);

  },



  deleteTcBankAccount: (id) => {

    return apiClient.delete(apiConfig.endpoints.tcBankAccountById(id));

  },



  // Own-account transfer between two TC accounts (body snake_case)
  createTcBankTransfer: (data) => apiClient.post(apiConfig.endpoints.tcBankTransfers, data),

  // Undo a transfer recorded by mistake (TC admin)
  reverseTcBankTransfer: (transferId, reason) => apiClient.post(apiConfig.endpoints.tcBankTransferReverse(transferId), { reason }),

  setDefaultTcBankAccount: (id) => {

    return apiClient.post(apiConfig.endpoints.tcBankAccountSetDefault(id));

  },



  setTcBankAccountActive: (id, active) => {

    return apiClient.patch(apiConfig.endpoints.tcBankAccountActive(id), { active });

  },



  // Customer Wallets

  getCustomerWallets: (typingCenterId) => {

    const params = typingCenterId ? { typingCenterId } : {};

    return apiClient.get(apiConfig.endpoints.customerWallets, { params });

  },



  getCustomerWallet: (id) => {

    return apiClient.get(apiConfig.endpoints.customerWalletById(id));

  },



  getCustomerWalletByOrg: (orgId) => {

    return apiClient.get(apiConfig.endpoints.customerWalletByOrg(orgId));

  },

  // params: type, direction, status, startDate, endDate (yyyy-MM-dd), page, size
  getCustomerWalletTransactions: (id, params = {}) => {
    return apiClient.get(apiConfig.endpoints.customerWalletTransactions(id), { params });
  },

  getCustomerWalletTransactionsSummary: (id) => {
    return apiClient.get(apiConfig.endpoints.customerWalletTransactionsSummary(id));
  },

  // Client (internal org) employees tracked in Thaklis Main (view-only, paginated)
  getClientEmployees: (orgId, page = 0, size = 20, statuses = null) => {
    const params = { page, size };
    if (statuses && statuses.length) params.status = statuses.join(',');
    return apiClient.get(apiConfig.endpoints.clientEmployees(orgId), { params });
  },

  updateClientEmployeeStatus: (orgId, uid, payload) => {
    return apiClient.patch(apiConfig.endpoints.clientEmployeeStatus(orgId, uid), payload);
  },

  getClientEmployeeDocuments: (orgId, uid) => {
    return apiClient.get(apiConfig.endpoints.clientEmployeeDocuments(orgId, uid));
  },

  viewClientEmployeeDocument: (orgId, filePath) => {
    return apiClient.get(apiConfig.endpoints.clientEmployeeDocumentView(orgId), {
      params: { filePath },
      responseType: 'blob'
    });
  },

  // Create-or-reuse a Thaklis employee for this client (org-scoped; mapped org resolved server-side)
  // Permanently delete one version (all its files) of a client employee's document.
  deleteClientEmployeeDocumentVersion: (orgId, uid, groupId) => {
    return apiClient.delete(apiConfig.endpoints.clientEmployeeDocumentVersionDelete(orgId, uid, groupId));
  },

  createClientEmployee: (orgId, payload) => {
    return apiClient.post(apiConfig.endpoints.clientEmployeeCreate(orgId), payload);
  },

  // Upload one or more files of the SAME document type to a client's employee (org-scoped segment)
  uploadClientEmployeeDocuments: (orgId, uid, files, documentType, metadata = {}) => {
    const formData = new FormData();
    (Array.isArray(files) ? files : [files]).forEach(f => formData.append('files', f));
    formData.append('documentType', documentType);
    if (metadata.documentNumber) formData.append('documentNumber', metadata.documentNumber);
    if (metadata.issueDate) formData.append('issueDate', metadata.issueDate);
    if (metadata.expiryDate) formData.append('expiryDate', metadata.expiryDate);
    return apiClient.post(apiConfig.endpoints.clientEmployeeDocumentUpload(orgId, uid), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },



  getCustomerWalletSummary: (typingCenterId) => {

    const params = typingCenterId ? { typingCenterId } : {};

    return apiClient.get(apiConfig.endpoints.customerWalletSummary, { params });

  },

  // Removed clients whose wallet still holds money to refund to the customer
  getWalletsPendingRefund: (typingCenterId) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.customerWalletsPendingRefund, { params });
  },

  // Return a client's wallet balance to the customer, paid from a TC bank ({ organizationId, bankAccountId, amount? })
  refundCustomerWallet: (payload) => {
    return apiClient.post(apiConfig.endpoints.customerWalletRefund, payload);
  },



  // Wallet Top-Up Requests (Managers)

  getTopUpRequests: (typingCenterId) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.topupRequests, { params });
  },

  getTopUpRequestById: (id) => {
    return apiClient.get(apiConfig.endpoints.topupRequestById(id));
  },

  // bankReference (optional, max 100) sets or corrects the bank reference of the request; without one the body is empty
  approveTopUpRequest: (id, bankReference) => {
    return apiClient.post(apiConfig.endpoints.topupRequestApprove(id), bankReference ? { bank_reference: bankReference } : undefined);
  },

  rejectTopUpRequest: (id, rejectionReason) => {
    return apiClient.post(apiConfig.endpoints.topupRequestReject(id), { rejection_reason: rejectionReason });
  },

  getTopUpRequestProofUrl: (id) => {
    return apiClient.get(apiConfig.endpoints.topupRequestProofUrl(id));
  },

  // Wallet Top-Up Requests (Portal)

  // TC-entered top-up on a customer's wallet (reviewed in the normal approve/reject flow).
  submitWalletTopUp: (walletId, { tcBankAccountId, amount, remarks, bankReference }, file) => {
    const formData = new FormData();
    formData.append('data', new Blob([JSON.stringify({
      tc_bank_account_id: tcBankAccountId,
      amount,
      remarks: remarks || undefined,
      bank_reference: bankReference || undefined
    })], { type: 'application/json' }));
    if (file) formData.append('file', file);
    return apiClient.post(apiConfig.endpoints.walletTopUpSubmit(walletId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  submitPortalTopUp: ({ tcBankAccountId, amount, remarks, bankReference }, file) => {
    const formData = new FormData();
    formData.append('data', new Blob([JSON.stringify({
      tc_bank_account_id: tcBankAccountId,
      amount,
      remarks: remarks || undefined,
      bank_reference: bankReference || undefined
    })], { type: 'application/json' }));
    formData.append('file', file);
    return apiClient.post(apiConfig.endpoints.portalTopUpSubmit, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('portalToken')}`
      }
    });
  },

  getPortalTopUpRequests: () => {
    return apiClient.get(apiConfig.endpoints.portalTopUpRequests, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  cancelPortalTopUp: (id) => {
    return apiClient.delete(apiConfig.endpoints.portalTopUpCancel(id), {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalWallet: () => {
    return apiClient.get(apiConfig.endpoints.portalWallet, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalWalletBankAccounts: () => {
    return apiClient.get(apiConfig.endpoints.portalWalletBankAccounts, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },



  // Ticket Overview (Finance)

  getTicketOverview: (organizationIds = null) => {

    const payload = organizationIds ? { organizationIds } : {};

    return apiClient.post(apiConfig.endpoints.ticketOverview, payload);

  },



  // Invoice Configuration

  getInvoiceConfig: (typingCenterId) => {

    return apiClient.get(apiConfig.endpoints.invoiceConfig(typingCenterId));

  },



  setInvoiceConfig: (typingCenterId, configData) => {

    return apiClient.post(apiConfig.endpoints.invoiceConfig(typingCenterId), configData);

  },

  getWorkOrderConfig: (typingCenterId) => {
    return apiClient.get(apiConfig.endpoints.workOrderConfig(typingCenterId));
  },

  setWorkOrderConfig: (typingCenterId, configData) => {
    return apiClient.post(apiConfig.endpoints.workOrderConfig(typingCenterId), configData);
  },

  // Sample invoice PDF using the given (unsaved) config — for the config-page preview.
  previewInvoiceConfigPdf: (typingCenterId, configData) => {
    return apiClient.post(`/api/invoice-config/${typingCenterId}/preview-pdf`, configData, {
      responseType: 'blob'
    });
  },



  deleteInvoiceConfig: (typingCenterId) => {

    return apiClient.delete(apiConfig.endpoints.invoiceConfig(typingCenterId));

  },



  uploadInvoiceLogo: (typingCenterId, formData) => {

    return apiClient.post(apiConfig.endpoints.invoiceConfigLogo(typingCenterId), formData, {

      headers: { 'Content-Type': 'multipart/form-data' }

    });

  },



  addPartnerLogo: (typingCenterId, formData) => {

    return apiClient.post(apiConfig.endpoints.invoiceConfigPartnerLogo(typingCenterId), formData, {

      headers: { 'Content-Type': 'multipart/form-data' }

    });

  },



  removePartnerLogo: (typingCenterId, logoKey) => {

    return apiClient.delete(apiConfig.endpoints.invoiceConfigPartnerLogo(typingCenterId), {

      data: { logoKey }

    });

  },



  getAllInvoiceConfigs: () => {

    return apiClient.get(apiConfig.endpoints.allInvoiceConfigs);

  },



  // Invoices

  getInvoices: (params = {}) => {

    return apiClient.get(apiConfig.endpoints.invoices, { params });

  },

  /** Invoices eligible for TC → Thaklis payment (Payments page). */
  getInvoicesPayableToThaklis: (typingCenterId) => {
    return apiClient.get('/api/invoices/payable-to-thaklis', { params: { typingCenterId } });
  },

  /** Outstanding invoices for a customer org — FINAL + customer_payment_status IN (UNPAID, PARTIAL).
   *  Used by the Settlements wizard and the portal multi-invoice pay flow. */
  getOutstandingInvoicesForOrg: (internalOrganizationId) => {
    return apiClient.get(`/api/invoices/outstanding/${internalOrganizationId}`);
  },

  /** Batched invoice totals per customer org — invoiced / collected / outstanding
   *  and FINAL+DRAFT counts, in one request. Max 200 ids. */
  getInvoiceOrgSummary: (internalOrganizationIds, typingCenterId) => {
    const params = {};
    if (typingCenterId) params.typingCenterId = typingCenterId;
    return apiClient.post('/api/invoices/org-summary', internalOrganizationIds, { params });
  },

  /** Employee counts per client org, keyed by org id, in one request. Max 100 ids. */
  getInternalOrgEmployeeCounts: (internalOrganizationIds, typingCenterId) => {
    const params = {};
    if (typingCenterId) params.typingCenterId = typingCenterId;
    return apiClient.post('/api/internal-organizations/employee-counts', internalOrganizationIds, { params });
  },

  // ==================== Invoice Payments & Receipts ====================

  /** TC settlement create (initiator=TC). Body: { internal_organization_id,
   *  settlement_mode: AUTO|CUSTOM, invoice_ids?, notes }.
   *  AUTO ignores invoice_ids; CUSTOM requires it. Returns { payment, receipts }. */
  createInvoicePaymentTC: (data) => {
    return apiClient.post('/api/invoice-payments', data);
  },

  /** List TC-side invoice payments (settlements). */
  getInvoicePayments: (params = {}) => {
    return apiClient.get('/api/invoice-payments', { params });
  },

  /** Single invoice payment with its receipts: returns { payment, receipts }. */
  getInvoicePayment: (id) => {
    return apiClient.get(`/api/invoice-payments/${id}`);
  },

  /** Receipts list (TC-scoped). */
  getReceipts: (typingCenterId) => {
    return apiClient.get('/api/receipts', { params: { typingCenterId } });
  },

  /** Single receipt detail. */
  getReceiptById: (id) => {
    return apiClient.get(`/api/receipts/${id}`);
  },

  /** Receipts (active + voided) for an invoice — used by InvoiceDetail
   *  Customer Payment History block. */
  getReceiptsByInvoice: (invoiceId) => {
    return apiClient.get(`/api/receipts/by-invoice/${invoiceId}`);
  },

  /** Void a receipt with reason (TC_ADMIN). */
  voidReceipt: (id, reason) => {
    return apiClient.post(`/api/receipts/${id}/void`, { reason });
  },

  // ==================== Portal Invoice Payments ====================

  /** Customer portal pay — POST /api/portal/invoice-payments.
   *  Body: { invoice_ids, expected_total }. Backend validates that the
   *  expected_total matches the computed allocation total. */
  payPortalInvoices: ({ invoiceIds, expectedTotal }) => {
    return apiClient.post(
      '/api/portal/invoice-payments',
      { invoice_ids: invoiceIds, expected_total: expectedTotal },
      { headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` } }
    );
  },

  /** Portal-scoped outstanding invoices — backend derives the org
   *  id from the portal JWT, so no path param. */
  getPortalOutstandingInvoicesForOrg: () => {
    return apiClient.get('/api/portal/invoices/outstanding', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  /** Portal — list all invoice payments for the caller's org (both
   *  CUSTOMER and TC initiated). Backend derives the org id from the
   *  portal JWT. */
  listPortalInvoicePayments: () => {
    return apiClient.get('/api/portal/invoice-payments', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },



  getInvoiceById: (id) => {

    return apiClient.get(apiConfig.endpoints.invoiceById(id));

  },



  createInvoice: (invoiceData) => {

    return apiClient.post(apiConfig.endpoints.invoices, invoiceData);

  },

  // Create invoice from work order
  // payload: { organizationId, organizationName, organizationCode } — the mapped external org to bill
  createInvoiceFromWorkOrder: (workOrderId, payload) => {
    return apiClient.post(`/api/invoices/from-work-order/${workOrderId}`, payload || {});
  },



  updateInvoice: (id, invoiceData) => {

    return apiClient.put(apiConfig.endpoints.invoiceById(id), invoiceData);

  },



  deleteInvoice: (id) => {

    return apiClient.delete(apiConfig.endpoints.invoiceById(id));

  },



  getInvoiceStatistics: (typingCenterId = null, dates = null) => {

    const params = { ...(typingCenterId && { typingCenterId }), ...(dates?.startDate && { startDate: dates.startDate }), ...(dates?.endDate && { endDate: dates.endDate }) };

    return apiClient.get(apiConfig.endpoints.invoiceStatistics, { params });

  },



  addTicketsToInvoice: (invoiceId, tickets) => {

    return apiClient.post(`/api/invoices/${invoiceId}/line-items`, { tickets });

  },



  finalizeInvoice: (id) => {

    return apiClient.post(apiConfig.endpoints.invoiceFinalize(id));

  },



  voidInvoice: (id, reason) => {

    return apiClient.post(apiConfig.endpoints.invoiceVoid(id), { reason });

  },



  markInvoicePaid: (id, amount = null) => {

    return apiClient.post(apiConfig.endpoints.invoiceMarkPaid(id), { amount });

  },



  // Markup endpoints (invoice level - legacy)

  applyInvoiceMarkup: (id, markupData) => {

    return apiClient.put(`/api/invoices/${id}/markup`, markupData);

  },



  removeInvoiceMarkup: (id, lineItemId = null) => {

    const params = lineItemId ? { lineItemId } : {};

    return apiClient.delete(`/api/invoices/${id}/markup`, { params });

  },



  // Line item markup endpoints (per-ticket)

  applyLineItemMarkup: (lineItemId, markupData) => {

    const payload = typeof markupData === 'number' ? { markupAmount: markupData } : (markupData || {});

    return apiClient.put(`/api/invoices/line-items/${lineItemId}/markup`, payload);

  },



  removeLineItemMarkup: (lineItemId) => {

    return apiClient.delete(`/api/invoices/line-items/${lineItemId}/markup`);

  },



  getInvoicePdf: (id, regenerate = false) => {

    return apiClient.get(apiConfig.endpoints.invoicePdf(id), {

      params: { regenerate }

    });

  },

  getInvoicePdfView: (id, params = {}) => {
    return apiClient.get(`/api/invoices/${id}/pdf/view`, {
      responseType: 'blob',
      params
    });
  },

  // Multi-invoice summary statement for one internal organization (filtered by status + date).
  getInvoiceSummaryPdf: (params = {}) => {
    return apiClient.get('/api/invoices/summary/pdf', {
      responseType: 'blob',
      params
    });
  },



  downloadInvoicePdf: (id) => {
    return apiClient
      .get(`/api/invoices/${id}/pdf/view`, { responseType: 'blob' })
      .then((res) => {
        const blobUrl = window.URL.createObjectURL(res.data);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
        return res;
      });
  },



  checkTicketInvoiced: (ticketId) => {

    return apiClient.get(apiConfig.endpoints.checkTicketInvoiced(ticketId));

  },



  getInvoicedTicketIds: (organizationId, typingCenterId = null) => {

    const params = typingCenterId ? { typingCenterId } : {};

    return apiClient.get(apiConfig.endpoints.invoicedTickets(organizationId), { params });

  },



  // Unpaid Tickets (for Sync to Thaklis)

  getUnpaidTickets: (organizationId, typingCenterId = null) => {

    const params = typingCenterId ? { typingCenterId } : {};

    return apiClient.get(`/api/tickets/organization/${organizationId}/unpaid`, { params });

  },



  // Sync Payment to Thaklis

  syncPaymentToThaklis: (paymentId, syncData) => {

    return apiClient.post(`/api/payments/${paymentId}/sync-to-thaklis`, syncData);

  },



  // Upload payment proof to Manager S3 (saves url/s3Key/filename on TPayment)
  uploadPaymentProofToManager: (paymentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/api/payments/${paymentId}/upload-proof`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Upload payment proof to Thaklis (proxied through /api/thaklis/upload-topup-proof)
  uploadPaymentProofToThaklis: (topupRequestId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/thaklis/upload-topup-proof', formData, {
      params: { topupRequestId },
      headers: { 'Content-Type': 'multipart/form-data' }
    });

  },

  // Initiate an online (NGenius) TC→Thaklis payment. Response includes
  // data.paymentUrl which the caller uses to redirect the user to the
  // NGenius hosted payment page.
  initiateOnlinePayment: (paymentData) => {
    return apiClient.post('/api/payments/initiate-online', paymentData);
  },

  // NGenius online platform fee (percentage + fixed), read from Thaklis Main.
  getOnlinePlatformFee: () => {
    return apiClient.get('/api/payments/online-platform-fee');
  },



  // Thaklis Services (for external service lookup)

  getServiceGroups: () => {

    return apiClient.get('/api/thaklis/service-groups');

  },



  getServices: (groupId = null) => {

    const params = groupId ? { groupId } : {};

    return apiClient.get('/api/thaklis/services', { params });

  },



  getServiceById: (serviceId) => {

    return apiClient.get(`/api/thaklis/services/${serviceId}`);

  },

  // Document Types from Thaklis
  getDocumentTypes: (scope = null) => {
    const params = scope ? { scope } : {};
    return apiClient.get('/api/thaklis/document-types', { params });
  },

  // Mapped Thaklis organizations for a typing center (id = Thaklis organization UUID)
  getMappings: (typingCenterId) => {
    return apiClient.get('/api/mappings', { params: { typingCenterId } });
  },

  // TC Document Expiry — CSV export (blob) of the currently-filtered report
  exportDocumentExpiry: (params) => {
    return apiClient.get('/api/document-expiry/export', { params, responseType: 'blob' });
  },

  // TC Document Expiry — email the currently-filtered report from the TC's configured mail account
  emailDocumentExpiry: (payload) => {
    return apiClient.post('/api/document-expiry/email', payload);
  },

  // Extract document fields via Thaklis OCR (generalized prompt by default).
  // selectedDocumentType is recorded in the Managers usage table only (not forwarded to Thaklis).
  extractDocument: (file, documentType = 'GENERAL', selectedDocumentType = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (selectedDocumentType) formData.append('selectedDocumentType', selectedDocumentType);
    return apiClient.post('/api/thaklis/documents/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Create-or-reuse a Thaklis employee under the mapped org (subscription cap bypassed)
  createOrReuseEmployee: (payload) => {
    return apiClient.post('/api/thaklis/employees/create-or-reuse', payload);
  },

  // Upload + attach one or more files of the SAME document type to a Thaklis employee
  // in a single bulk batch (all become the latest version), with shared extracted metadata.
  uploadEmployeeDocuments: (uid, files, documentType, metadata = {}, segment = null) => {
    const formData = new FormData();
    (Array.isArray(files) ? files : [files]).forEach(f => formData.append('files', f));
    formData.append('documentType', documentType);
    if (metadata.documentNumber) formData.append('documentNumber', metadata.documentNumber);
    if (metadata.issueDate) formData.append('issueDate', metadata.issueDate);
    if (metadata.expiryDate) formData.append('expiryDate', metadata.expiryDate);
    if (segment) formData.append('segment', segment);
    return apiClient.post(`/api/thaklis/employees/${uid}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },



  // External Services (for invoices)

  addExternalService: (invoiceId, serviceData) => {

    return apiClient.post(`/api/invoices/${invoiceId}/external-services`, serviceData);

  },



  removeLineItem: (invoiceId, lineItemId) => {

    return apiClient.delete(`/api/invoices/${invoiceId}/line-items/${lineItemId}`);

  },

  reorderInvoiceLineItems: (invoiceId, lineItemIds) => {
    return apiClient.put(`/api/invoices/${invoiceId}/line-items/order`, { line_item_ids: lineItemIds });
  },


  updateLineItemCustomerName: (invoiceId, lineItemId, customerName) => {
    return apiClient.patch(`/api/invoices/${invoiceId}/line-items/${lineItemId}`, {
      customer_name: customerName
    });
  },

  // Patch any of customer_name / description / quantity on a line item.
  updateLineItem: (invoiceId, lineItemId, patch) => {
    return apiClient.patch(`/api/invoices/${invoiceId}/line-items/${lineItemId}`, patch);
  },

  // Update invoice organization name (calls jserver directly on port 5001)
  updateInvoiceOrganizationName: (invoiceId, organizationName) => {

    return apiClient.put(`/api/invoices/${invoiceId}/organization-name`, { organizationName });

  },

  // Quotations
  // Platform last-active report (typing-centre + client users)
  getLastActiveUsers: (params = {}) => {
    return apiClient.get(apiConfig.endpoints.lastActiveUsers, { params });
  },

  // Platform storage-usage report (per typing centre)
  getStorageUsage: (params = {}) => {
    return apiClient.get(apiConfig.endpoints.storageTypingCenters, { params });
  },
  recomputeStorageUsage: () => {
    return apiClient.post(apiConfig.endpoints.storageRecompute);
  },

  getQuotations: (params = {}) => {
    return apiClient.get(apiConfig.endpoints.quotations, { params });
  },

  getQuotationById: (id) => {
    return apiClient.get(apiConfig.endpoints.quotationById(id));
  },

  getQuotationPdfView: (id, params = {}) => {
    return apiClient.get(`/api/quotations/${id}/pdf/view`, { responseType: 'blob', params });
  },

  createQuotation: (quotationData) => {
    return apiClient.post(apiConfig.endpoints.quotations, quotationData);
  },

  updateQuotation: (id, quotationData) => {
    return apiClient.put(apiConfig.endpoints.quotationById(id), quotationData);
  },

  deleteQuotation: (id) => {
    return apiClient.delete(apiConfig.endpoints.quotationById(id));
  },

  getQuotationStatistics: (typingCenterId = null, dates = null) => {
    const params = { ...(typingCenterId && { typingCenterId }), ...(dates?.startDate && { startDate: dates.startDate }), ...(dates?.endDate && { endDate: dates.endDate }) };
    return apiClient.get(apiConfig.endpoints.quotationStatistics, { params });
  },

  finalizeQuotation: (id) => {
    return apiClient.post(apiConfig.endpoints.quotationFinalize(id));
  },

  sendQuotation: (id, sendData = {}) => {
    return apiClient.post(apiConfig.endpoints.quotationSend(id), sendData);
  },

  acceptQuotation: (id) => {
    return apiClient.post(apiConfig.endpoints.quotationAccept(id));
  },

  rejectQuotation: (id, reason = null) => {
    return apiClient.post(apiConfig.endpoints.quotationReject(id), { reason });
  },

  convertQuotation: (id, convertData) => {
    return apiClient.post(apiConfig.endpoints.quotationConvert(id), convertData);
  },

  addQuotationLineItem: (quotationId, itemData) => {
    return apiClient.post(`/api/quotations/${quotationId}/line-items`, itemData);
  },

  removeQuotationLineItem: (quotationId, lineItemId) => {
    return apiClient.delete(`/api/quotations/${quotationId}/line-items/${lineItemId}`);
  },

  applyQuotationLineItemMarkup: (lineItemId, payload) => {
    // payload: { markupAmount, taxOnMarkup }
    return apiClient.put(`/api/quotations/line-items/${lineItemId}/markup`, payload);
  },

  removeQuotationLineItemMarkup: (lineItemId) => {
    return apiClient.delete(`/api/quotations/line-items/${lineItemId}/markup`);
  },

  // Work Orders
  getWorkOrders: (params = {}) => {
    return apiClient.get(apiConfig.endpoints.workOrders, { params });
  },

  getWorkOrderById: (id) => {
    return apiClient.get(apiConfig.endpoints.workOrderById(id));
  },

  createWorkOrder: (workOrderData) => {
    return apiClient.post(apiConfig.endpoints.workOrders, workOrderData);
  },

  createWorkOrderFromQuotation: (quotationId) => {
    return apiClient.post(apiConfig.endpoints.workOrderFromQuotation(quotationId));
  },

  updateWorkOrder: (id, workOrderData) => {
    return apiClient.put(apiConfig.endpoints.workOrderById(id), workOrderData);
  },

  getWorkOrderStatistics: (typingCenterId = null, dates = null) => {
    const params = { ...(typingCenterId && { typingCenterId }), ...(dates?.startDate && { startDate: dates.startDate }), ...(dates?.endDate && { endDate: dates.endDate }) };
    return apiClient.get(apiConfig.endpoints.workOrderStatistics, { params });
  },

  searchPastCustomers: (organizationId, search) => {
    return apiClient.get('/api/work-orders/customers/search', { params: { organizationId, search } });
  },

  // Approve a PENDING_REVIEW work order (submitted from portal) → transitions to OPEN.
  // Requires at least one line item on the work order (enforced by the backend).
  approveWorkOrder: (id) => {
    return apiClient.post(`/api/work-orders/${id}/approve`);
  },

  startWorkOrder: (id) => {
    return apiClient.post(apiConfig.endpoints.workOrderStart(id));
  },

  holdWorkOrder: (id) => {
    return apiClient.post(apiConfig.endpoints.workOrderHold(id));
  },

  completeWorkOrder: (id) => {
    return apiClient.post(apiConfig.endpoints.workOrderComplete(id));
  },

  cancelWorkOrder: (id, reason = null) => {
    return apiClient.delete(apiConfig.endpoints.workOrderCancel(id), { data: { reason } });
  },

  markWorkOrderInvoiced: (id, invoiceData) => {
    return apiClient.post(apiConfig.endpoints.workOrderMarkInvoiced(id), invoiceData);
  },

  // Work Order Line Items (individual add/delete)
  addWorkOrderLineItem: (workOrderId, itemData) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/line-items`, itemData);
  },

  // Add a new customer (applicant) with all of their services in one call — { customer, line_items }.
  addWorkOrderCustomer: (workOrderId, payload) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/customers`, payload);
  },

  // Replace a line item's service / applicant / amounts (blocked if its task or internal task is completed).
  updateWorkOrderLineItem: (workOrderId, lineItemId, itemData) => {
    return apiClient.put(`/api/work-orders/${workOrderId}/line-items/${lineItemId}`, itemData);
  },

  // ---- On-behalf work orders (beta; provider creates + delegates for a connected client TC) ----
  getOnBehalfClients: () => apiClient.get('/api/on-behalf-work-orders/clients'),
  getOnBehalfClientOrganizations: (connectionId) =>
    apiClient.get('/api/on-behalf-work-orders/client-organizations', { params: { connectionId } }),
  getOnBehalfClientServices: (connectionId, organizationId) =>
    apiClient.get('/api/on-behalf-work-orders/client-services',
      { params: { connectionId, ...(organizationId ? { organizationId } : {}) } }),
  getOnBehalfProviderServices: (connectionId) =>
    apiClient.get('/api/on-behalf-work-orders/provider-services', { params: { connectionId } }),
  createOnBehalfWorkOrder: (request) => apiClient.post('/api/on-behalf-work-orders', request),
  uploadOnBehalfDocument: (workOrderId, connectionId, file, customerName, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    if (customerName) formData.append('customerName', customerName);
    if (documentType) formData.append('documentType', documentType);
    return apiClient.post(`/api/on-behalf-work-orders/${workOrderId}/documents`, formData, {
      params: { connectionId },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // files = [{ lineItemId, file, documentType }] — manual provider documents, uploaded onto the
  // provider WO only (their doc types are from the provider catalog, not the client's).
  delegateOnBehalfWorkOrder: (workOrderId, connectionId, lineItemProviderServices, lineItemFieldValues = {}, files = [], lineItemDocIds = {}, lineItemDocOverrides = {}) => {
    const formData = new FormData();
    formData.append('line_item_provider_services', JSON.stringify(lineItemProviderServices));
    formData.append('line_item_field_values', JSON.stringify(lineItemFieldValues));
    formData.append('line_item_doc_ids', JSON.stringify(lineItemDocIds));
    formData.append('line_item_doc_overrides', JSON.stringify(lineItemDocOverrides));
    files.forEach((f) => {
      formData.append('files', f.file);
      formData.append('file_line_item_ids', f.lineItemId);
      formData.append('file_doc_types', f.documentType || '');
    });
    return apiClient.post(`/api/on-behalf-work-orders/${workOrderId}/delegate`, formData,
      { params: { connectionId }, headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // On-behalf allowed-orgs (beta — removable): a client TC picks which of its orgs the provider may use.
  getOnBehalfAllowedOrgs: () => apiClient.get('/api/onbehalf-allowed-orgs'),
  updateOnBehalfAllowedOrgs: (data) => apiClient.put('/api/onbehalf-allowed-orgs', data),

  removeWorkOrderLineItem: (workOrderId, lineItemId) => {
    return apiClient.delete(`/api/work-orders/${workOrderId}/line-items/${lineItemId}`);
  },

  applyWorkOrderLineItemMarkup: (lineItemId, payload) => {
    // payload: { markupAmount, taxOnMarkup }
    return apiClient.put(`/api/work-orders/line-items/${lineItemId}/markup`, payload);
  },

  removeWorkOrderLineItemMarkup: (lineItemId) => {
    return apiClient.delete(`/api/work-orders/line-items/${lineItemId}/markup`);
  },

  // Work Order Tasks
  addWorkOrderTask: (workOrderId, taskData) => {
    return apiClient.post(apiConfig.endpoints.workOrderTasks(workOrderId), taskData);
  },

  updateWorkOrderTask: (workOrderId, taskId, taskData) => {
    return apiClient.put(apiConfig.endpoints.workOrderTaskById(workOrderId, taskId), taskData);
  },

  completeWorkOrderTask: (workOrderId, taskId) => {
    return apiClient.post(apiConfig.endpoints.workOrderTaskComplete(workOrderId, taskId));
  },

  reopenWorkOrderTask: (workOrderId, taskId) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/tasks/${taskId}/reopen`);
  },

  deleteWorkOrderTask: (workOrderId, taskId) => {
    return apiClient.delete(apiConfig.endpoints.workOrderTaskById(workOrderId, taskId));
  },

  // Work Order Documents
  getWorkOrderDocuments: (workOrderId) => {
    return apiClient.get(apiConfig.endpoints.workOrderDocuments(workOrderId));
  },

  // Blob through the authenticated client: a plain link cannot carry the JWT header.
  downloadWorkOrderDocument: (workOrderId, documentId) => {
    return apiClient.get(`/api/work-orders/${workOrderId}/documents/${documentId}/download`, {
      responseType: 'blob'
    });
  },

  uploadWorkOrderDocument: (workOrderId, file, description = '', documentType = 'REFERENCE', customerName = null, workOrderTaskId = null, transactionNumber = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    formData.append('documentType', documentType);
    if (customerName) {
      formData.append('customerName', customerName);
    }
    // Links the document to a service (WO task) as its deliverable; mirrors to the client on provider uploads.
    if (workOrderTaskId) {
      formData.append('workOrderTaskId', workOrderTaskId);
    }
    // With a task: the service's full list of transaction numbers (comma-separated, up to 3); replaces its current ones.
    if (transactionNumber) {
      formData.append('transactionNumber', transactionNumber);
    }
    return apiClient.post(apiConfig.endpoints.workOrderDocuments(workOrderId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Extract only the application/transaction number from a single deliverable document via the TC's
  // OpenAI key. Resolves to { application_number: <value|null> }; rejects with an error on key/network issues.
  extractWorkOrderApplicationNumber: (workOrderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/api/work-orders/${workOrderId}/extract-application-number`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getWorkOrderDocumentDownloadUrl: (workOrderId, documentId) => {
    return apiClient.get(apiConfig.endpoints.workOrderDocumentDownload(workOrderId, documentId));
  },

  // Suggested recipients (internal org + WO customer email) for a task's deliverable email.
  getWorkOrderSuggestedRecipients: (workOrderId) => {
    return apiClient.get(apiConfig.endpoints.workOrderSuggestedRecipients(workOrderId));
  },

  // Email a completed task's linked documents; subject/body come from the WORK_ORDER_COMPLETED template.
  sendWorkOrderTaskEmail: (workOrderId, taskId, recipients, cc = []) => {
    return apiClient.post(apiConfig.endpoints.workOrderTaskSendEmail(workOrderId, taskId), { recipients, cc });
  },

  deleteWorkOrderDocument: (workOrderId, documentId) => {
    return apiClient.delete(apiConfig.endpoints.workOrderDocumentById(workOrderId, documentId));
  },

  // Ticket linking for tasks
  linkTaskToTicket: (workOrderId, taskId, ticketData) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/tasks/${taskId}/link-ticket`, ticketData);
  },

  unlinkTaskFromTicket: (workOrderId, taskId) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/tasks/${taskId}/unlink-ticket`);
  },

  // ---- B2B hand-off (client TC → a connected B2B) ----
  // The selected B2B's internal services priced for this client (the "Give to B2B" picker).
  getB2bServices: (connectionId) => {
    return apiClient.get('/api/work-orders/b2b-services', { params: { connectionId } });
  },

  // documentIds = existing WO doc ids to copy to the B2B WO; files = [{file, documentType}] uploaded
  // manually — sent server-side onto the delegated (provider) WO only.
  handoffTaskToB2b: (workOrderId, taskId, connectionId, b2bServiceId, documentIds = [], fieldValues = [], files = [], documentTypeOverrides = {}) => {
    const formData = new FormData();
    formData.append('connectionId', connectionId);
    formData.append('b2b_service_id', b2bServiceId);
    formData.append('document_ids', JSON.stringify(documentIds));
    formData.append('field_values', JSON.stringify(fieldValues));
    formData.append('document_type_overrides', JSON.stringify(documentTypeOverrides));
    files.forEach((f) => {
      formData.append('files', f.file);
      formData.append('file_doc_types', f.documentType || '');
    });
    return apiClient.post(`/api/work-orders/${workOrderId}/tasks/${taskId}/handoff-b2b`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  unlinkTaskFromB2b: (workOrderId, taskId) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/tasks/${taskId}/unlink-b2b`);
  },

  getTaskB2bStatus: (workOrderId, taskId) => {
    return apiClient.get(`/api/work-orders/${workOrderId}/tasks/${taskId}/b2b-status`);
  },
  getWorkOrderB2bStatuses: (workOrderId) => {
    return apiClient.get(`/api/work-orders/${workOrderId}/tasks/b2b-status`);
  },

  // Assign all internal tasks in a work order to an employee
  assignWorkOrder: (workOrderId, assignmentData) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/assign`, assignmentData);
  },

  // Unassign all internal tasks in a work order
  unassignWorkOrder: (workOrderId) => {
    return apiClient.post(`/api/work-orders/${workOrderId}/unassign`);
  },

  // ==================== Multi-Work Order Invoice ====================

  /**
   * Get invoiceable work orders for an organization
   * Returns COMPLETED work orders that haven't been invoiced yet
   */
  getInvoiceableWorkOrders: async (organizationId, typingCenterId) => {
    try {
      // Get all work orders for organization
      const response = await apiClient.get('/api/work-orders', {
        params: { organizationId, typingCenterId, limit: 100 }
      });
      const allWorkOrders = response.data?.data || [];

      // Filter to COMPLETED and non-invoiced only
      const invoiceable = allWorkOrders.filter(wo => {
        const status = (wo.status || '').toUpperCase();
        const hasInvoice = wo.invoiceId || wo.invoice_id;
        return status === 'COMPLETED' && !hasInvoice;
      });

      return { data: { data: invoiceable } };
    } catch (err) {
      console.error('Error fetching invoiceable work orders:', err);
      return { data: { data: [] } };
    }
  },

  /**
   * Create invoice from multiple work orders
   * @param {Object} data - { typingCenterId, organizationId, organizationName, workOrderIds: [] }
   */
  createInvoiceFromWorkOrders: (data) => {
    // Backend endpoint for creating invoice from multiple work orders
    return apiClient.post('/api/invoices/from-work-orders', data);
  },

  /**
   * Get work orders linked to an invoice
   */
  getInvoiceWorkOrders: (invoiceId) => {
    // Get work orders for this invoice
    return apiClient.get(`/api/invoices/${invoiceId}/work-orders`);
  },

  // Get tickets for organization (from Thaklis)
  getTicketsForOrganization: (organizationId) => {
    return apiClient.get(`/api/tickets/organization/${organizationId}`);
  },

  // ==================== Group Owner APIs ====================

  // Group-owner login (multi-TC owner). Distinct token stored as groupToken.
  groupLogin: (username, password) => {
    return apiClient.post('/api/group/auth/login', { username, password });
  },

  // The owner's typing centers (group token).
  getGroupCenters: () => {
    return apiClient.get('/api/group/typing-centers', {
      headers: { Authorization: `Bearer ${localStorage.getItem('groupToken')}` }
    });
  },

  // Group-owner aggregate dashboard summary (local Thaklis Pro data only).
  getGroupDashboardSummary: () => {
    return apiClient.get('/api/group/dashboard/summary', {
      headers: { Authorization: `Bearer ${localStorage.getItem('groupToken')}` }
    });
  },

  // Open one of the owner's TCs — returns an acting TC_ADMIN token (group token).
  groupSwitch: (typingCenterId) => {
    return apiClient.post(`/api/group/switch/${typingCenterId}`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('groupToken')}` }
    });
  },

  // ==================== Portal Group APIs ====================

  getPortalGroupCustomers: (params = {}) => {
    return apiClient.get('/api/portal-group/customers', {
      params,
      headers: { Authorization: `Bearer ${localStorage.getItem('portalGroupToken')}` }
    });
  },

  portalGroupSwitch: (organizationId) => {
    return apiClient.post(`/api/portal-group/switch/${organizationId}`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalGroupToken')}` }
    });
  },

  portalGroupEnableCustomerPortal: (organizationId) => {
    return apiClient.post(`/api/portal-group/customers/${organizationId}/enable-portal`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalGroupToken')}` }
    });
  },

  getPortalCustomerGroups: (params = {}) =>
    apiClient.get('/api/internal-organization-groups', { params }),
  createPortalCustomerGroup: (payload) =>
    apiClient.post('/api/internal-organization-groups', payload),
  updatePortalCustomerGroup: (groupId, payload) =>
    apiClient.put(`/api/internal-organization-groups/${groupId}`, payload),
  getPortalCustomerGroupCustomers: (groupId, params = {}) =>
    apiClient.get(`/api/internal-organization-groups/${groupId}/customers`, { params }),
  attachPortalCustomerGroupCustomers: (groupId, organizationIds) =>
    apiClient.post(`/api/internal-organization-groups/${groupId}/customers/attach`, { organizationIds }),
  detachPortalCustomerGroupCustomers: (groupId, organizationIds) =>
    apiClient.post(`/api/internal-organization-groups/${groupId}/customers/detach`, { organizationIds }),
  getPortalCustomerGroupUsers: (groupId, params = {}) =>
    apiClient.get(`/api/internal-organization-groups/${groupId}/users`, { params }),
  createPortalCustomerGroupUser: (groupId, payload) =>
    apiClient.post(`/api/internal-organization-groups/${groupId}/users`, payload),
  setPortalCustomerGroupUserActive: (groupId, userId, active) =>
    apiClient.put(`/api/internal-organization-groups/${groupId}/users/${userId}/active`, { active }),

  // ==================== Portal APIs ====================

  // Portal Authentication
  portalLogin: (username, password) => {
    return apiClient.post('/api/portal/auth/login', { username, password });
  },

  // Cross-subdomain portal auto-login (promoted TC's TC_ADMIN → its B2B portal).
  // initiate runs in the promoted TC's staff session; exchange runs on the B2B subdomain.
  initiatePortalAutoLogin: (connectionId) => {
    return apiClient.post('/api/portal-auto-login/initiate', { connectionId });
  },

  exchangePortalAutoLoginToken: (token) => {
    return apiClient.post('/api/portal-auto-login/exchange', { token });
  },

  // ---- B2B partnerships (tenant connections) ----
  getTenantConnections: (status) => apiClient.get('/api/tenant-connections', { params: status ? { status } : {} }),
  getPendingConnections: () => apiClient.get('/api/tenant-connections/pending'),
  getB2bPartners: () => apiClient.get('/api/tenant-connections/b2b-partners'),
  searchB2bs: (query) => apiClient.get('/api/tenant-connections/b2bs', { params: { query } }),
  getB2bProfile: (typingCenterId) => apiClient.get(`/api/tenant-connections/profile/${typingCenterId}`),
  getOwnB2bProfile: () => apiClient.get('/api/tenant-connections/profile'),
  saveB2bProfile: (data) => apiClient.put('/api/tenant-connections/profile', data),
  setB2bDiscoverable: (discoverable) => apiClient.patch('/api/tenant-connections/profile/visibility', { discoverable }),
  requestTenantConnection: (providerB2bTypingCenterId, notes) =>
    apiClient.post('/api/tenant-connections', { provider_b2b_typing_center_id: providerB2bTypingCenterId, notes }),
  approveTenantConnection: (id) => apiClient.post(`/api/tenant-connections/${id}/approve`),
  rejectTenantConnection: (id, reason) => apiClient.post(`/api/tenant-connections/${id}/reject`, { reason }),
  cancelTenantConnection: (id) => apiClient.delete(`/api/tenant-connections/${id}`),
  disconnectTenantConnection: (id, reason) => apiClient.post(`/api/tenant-connections/${id}/disconnect`, { reason }),
  sendB2bInquiry: (data) => apiClient.post('/api/tenant-connections/inquiries', data),
  listB2bInquiries: (status) => apiClient.get('/api/tenant-connections/inquiries', { params: status ? { status } : {} }),
  markInquiryContacted: (id) => apiClient.patch(`/api/tenant-connections/inquiries/${id}`, {}),

  // Per-user B2B portal credential for auto-login (scoped to the caller).
  getPortalCredential: (connectionId) => apiClient.get(`/api/tenant-connections/${connectionId}/portal-credential`),
  savePortalCredential: (connectionId, username, password) =>
    apiClient.put(`/api/tenant-connections/${connectionId}/portal-credential`, { username, password }),
  verifyPortalCredential: (connectionId) => apiClient.post(`/api/tenant-connections/${connectionId}/portal-credential/verify`),
  deletePortalCredential: (connectionId) => apiClient.delete(`/api/tenant-connections/${connectionId}/portal-credential`),

  getPortalCurrentUser: () => {
    return apiClient.get('/api/portal/auth/me', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  changePortalPassword: (currentPassword, newPassword) => {
    return apiClient.post('/api/portal/auth/change-password', {
      currentPassword,
      newPassword
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal Quotations
  getPortalQuotations: () => {
    return apiClient.get('/api/portal/quotations', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalQuotation: (id) => {
    return apiClient.get(`/api/portal/quotations/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  acceptPortalQuotation: (id) => {
    return apiClient.post(`/api/portal/quotations/${id}/accept`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  rejectPortalQuotation: (id, reason) => {
    return apiClient.post(`/api/portal/quotations/${id}/reject`, { reason }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal Work Orders
  getPortalWorkOrders: () => {
    return apiClient.get('/api/portal/work-orders', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalWorkOrder: (id) => {
    return apiClient.get(`/api/portal/work-orders/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // params: status, workOrderId, customerName, search, page, size
  getPortalTasks: (params = {}) => {
    return apiClient.get(apiConfig.endpoints.portalTasks, {
      params,
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalTask: (id) => {
    return apiClient.get(apiConfig.endpoints.portalTaskById(id), {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalTaskEmployees: () => {
    return apiClient.get(apiConfig.endpoints.portalTaskEmployees, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalTaskDocuments: (id) => {
    return apiClient.get(apiConfig.endpoints.portalTaskDocuments(id), {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalTaskDocumentDownloadUrl: (id, documentId) => {
    return apiClient.get(apiConfig.endpoints.portalTaskDocumentDownload(id, documentId), {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal services — Thaklis services
  getPortalServices: () => {
    return apiClient.get('/api/portal/thaklis/services', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal internal services — TC's internal services
  getPortalInternalServices: () => {
    return apiClient.get('/api/portal/internal-services', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  createPortalWorkOrder: (data) => {
    return apiClient.post('/api/portal/work-orders', data, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal Document Types — returns the same list as /api/thaklis/document-types,
  // scoped to a portal token.
  getPortalDocumentTypes: (scope = null) => {
    const params = scope ? { scope } : {};
    return apiClient.get('/api/portal/thaklis/document-types', {
      params,
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal TC Document Types — the portal user's own typing center's document types
  // (TC resolved from the portal token); falls back to the Thaklis list if the TC
  // hasn't defined any. Used by the portal create-WO document upload.
  getPortalTcDocumentTypes: (holderType) => {
    return apiClient.get('/api/portal/tc-document-types', {
      params: holderType ? { holderType } : undefined,
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal Service Field Defaults
  getPortalFieldDefaults: () => {
    return apiClient.get('/api/portal/field-defaults', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  savePortalFieldDefaults: (defaults) => {
    return apiClient.put('/api/portal/field-defaults', { defaults }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal Work Order Document Upload
  uploadPortalWorkOrderDocument: (workOrderId, file, description = '', documentType = 'REFERENCE', customerName = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    formData.append('documentType', documentType);
    if (customerName) {
      formData.append('customerName', customerName);
    }
    return apiClient.post(`/api/portal/work-orders/${workOrderId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('portalToken')}`
      }
    });
  },

  // Portal Invoices
  getPortalInvoices: () => {
    return apiClient.get('/api/portal/invoices', {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal OCR extract (generalized prompt by default).
  // selectedDocumentType is recorded in the Managers usage table only (not forwarded to Thaklis).
  extractPortalDocument: (file, documentType = 'GENERAL', selectedDocumentType = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (selectedDocumentType) formData.append('selectedDocumentType', selectedDocumentType);
    return apiClient.post('/api/portal/documents/extract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('portalToken')}`
      }
    });
  },

  // Portal: create-or-reuse the applicant's Thaklis employee. The internal org (employee tag +
  // S3 segment) is forced server-side from the portal token.
  createOrReusePortalEmployee: (payload) => {
    return apiClient.post('/api/portal/employees/create-or-reuse', payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal: upload + attach files of one document type to an employee in a single bulk batch.
  // No segment is sent — the server always uses the caller's own internal org.
  uploadPortalEmployeeDocuments: (uid, files, documentType, metadata = {}) => {
    const formData = new FormData();
    (Array.isArray(files) ? files : [files]).forEach(f => formData.append('files', f));
    formData.append('documentType', documentType);
    if (metadata.documentNumber) formData.append('documentNumber', metadata.documentNumber);
    if (metadata.issueDate) formData.append('issueDate', metadata.issueDate);
    if (metadata.expiryDate) formData.append('expiryDate', metadata.expiryDate);
    return apiClient.post(`/api/portal/employees/${uid}/documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('portalToken')}`
      }
    });
  },

  // Portal: this organization's employees (tracked in Thaklis Main, tagged with the portal user's org).
  getPortalEmployees: (page = 0, size = 20) => {
    return apiClient.get('/api/portal/employees', {
      params: { page, size },
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal: one employee's documents, grouped with versions. Server checks the employee is this org's.
  getPortalEmployeeDocuments: (uid) => {
    return apiClient.get(`/api/portal/employees/${uid}/documents`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal: open one document file (PORTAL-EMPLOYEES-AND-FEATURES.md task PE2).
  viewPortalEmployeeDocument: (filePath) => {
    return apiClient.get('/api/portal/employees/documents/view', {
      params: { filePath },
      responseType: 'blob',
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  // Portal document-expiry report — auto-scoped to the portal user's own internal org.
  getPortalDocumentExpiry: (params) => {
    return apiClient.get('/api/portal/document-expiry', {
      params,
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalDocumentExpirySummary: (params) => {
    return apiClient.get('/api/portal/document-expiry/summary', {
      params,
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalInvoice: (id) => {
    return apiClient.get(`/api/portal/invoices/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` }
    });
  },

  getPortalInvoicePdf: (id) => {
    return apiClient.get(`/api/portal/invoices/${id}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` },
      responseType: 'blob'
    });
  },

  // ==================== Internal Organization Users Management ====================

  // List users for an internal organization
  getInternalOrgUsers: (orgId) => {
    return apiClient.get(`/api/internal-organizations/${orgId}/users`);
  },

  // Create a user for an internal organization
  createInternalOrgUser: (orgId, userData) => {
    return apiClient.post(`/api/internal-organizations/${orgId}/users`, userData);
  },

  // Get user details
  getInternalOrgUser: (orgId, userId) => {
    return apiClient.get(`/api/internal-organizations/${orgId}/users/${userId}`);
  },

  // Update a user
  updateInternalOrgUser: (orgId, userId, userData) => {
    return apiClient.put(`/api/internal-organizations/${orgId}/users/${userId}`, userData);
  },

  // Delete (deactivate) a user
  deleteInternalOrgUser: (orgId, userId) => {
    return apiClient.delete(`/api/internal-organizations/${orgId}/users/${userId}`);
  },

  // Reset user password
  resetInternalOrgUserPassword: (orgId, userId, password) => {
    return apiClient.post(`/api/internal-organizations/${orgId}/users/${userId}/reset-password`, { password });
  },

  // ==================== Email Configuration (Global) ====================

  // Get email config for a typing center
  getEmailConfig: (typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.emailConfig, { params });
  },

  // Update email config
  updateEmailConfig: (data, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.put(apiConfig.endpoints.emailConfig, data, { params });
  },

  // Get all email templates for a typing center
  getEmailTemplates: (typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.emailConfigTemplates, { params });
  },

  // Get a specific email template
  getEmailTemplate: (type, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.emailConfigTemplateByType(type), { params });
  },

  // Update an email template
  updateEmailTemplate: (type, data, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.put(apiConfig.endpoints.emailConfigTemplateByType(type), data, { params });
  },

  // Send test email
  sendTestEmail: (testEmail, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.post(apiConfig.endpoints.emailConfigTest, { email: testEmail }, { params });
  },

  // Get available template types
  getEmailTemplateTypes: () => {
    return apiClient.get(apiConfig.endpoints.emailConfigTemplateTypes);
  },

  // ==================== AI Configuration (per typing center) ====================

  // Get AI config (masked — never returns the raw key)
  getAiConfig: (typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.aiConfig, { params });
  },

  // Update AI config (send apiKey only when changing it)
  updateAiConfig: (data, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.put(apiConfig.endpoints.aiConfig, data, { params });
  },

  // Test a provider's stored key (OPENAI or ANTHROPIC; the backend defaults to OPENAI)
  testAiConnection: (typingCenterId = null, provider = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    if (provider) params.provider = provider;
    return apiClient.post(apiConfig.endpoints.aiConfigTest, null, { params });
  },

  // Notification preferences (which configurable events send)
  getNotificationPreferences: (typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.notificationPreferences, { params });
  },

  // Upsert one event's preference: { event_type, enabled, options }
  updateNotificationPreference: (data, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.put(apiConfig.endpoints.notificationPreferences, data, { params });
  },

  // ==================== Company Configuration (Global) ====================

  // Get company config for a typing center
  getCompanyConfig: (typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.get(apiConfig.endpoints.companyConfig, { params });
  },

  // Get company config by typing center ID
  getCompanyConfigById: (typingCenterId) => {
    return apiClient.get(apiConfig.endpoints.companyConfigById(typingCenterId));
  },

  // Update company config
  updateCompanyConfig: (data, typingCenterId = null) => {
    const params = typingCenterId ? { typingCenterId } : {};
    return apiClient.put(apiConfig.endpoints.companyConfig, data, { params });
  },

  // Update company config by typing center ID
  updateCompanyConfigById: (typingCenterId, data) => {
    return apiClient.post(apiConfig.endpoints.companyConfigById(typingCenterId), data);
  },

  // Upload company logo (used by Tenant Settings; returns { logo_url })
  uploadCompanyLogo: (typingCenterId, formData) => {
    return apiClient.post(apiConfig.endpoints.companyConfigLogo(typingCenterId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // ==================== RBAC: custom roles + team members ====================
  // A TC admin is scoped to its own TC; a platform admin passes typingCenterId to target one.
  getRoles: (params = {}) => apiClient.get('/api/roles', { params }),
  createRole: (data, typingCenterId = null) =>
    apiClient.post('/api/roles', data, { params: typingCenterId ? { typingCenterId } : {} }),
  updateRole: (id, data, typingCenterId = null) =>
    apiClient.put(`/api/roles/${id}`, data, { params: typingCenterId ? { typingCenterId } : {} }),
  archiveRole: (id, typingCenterId = null) =>
    apiClient.delete(`/api/roles/${id}`, { params: typingCenterId ? { typingCenterId } : {} }),
  // Team members (TC_ADMIN or platform). Payload uses role + role_id (TC_STAFF needs a same-TC role).
  createTeamMember: (payload) => apiClient.post('/api/users', payload),
  updateTeamMember: (id, payload) => apiClient.put(`/api/users/${id}`, payload)
};



export default ApiService;

